import { log, LogLevel } from 'utils/LogUtil';
import axios from './axiosInstance';
import {
  ConsultationRecord,
  ConsultationRecordAdmin,
  ConsultationRecordListItem,
  ConsultationContext,
  AdminConsultationListResponse,
  ConsultationRecordSummary,
  DrpSource,
  DrpFormat,
  DrpChecklistRow,
  EducationChecklistRow,
  TextConsultationContext,
} from '../common/models/types';

// ==================== 사용자용 API ====================

/**
 * 상담 기록 목록 조회 파라미터
 */
export interface ConsultationListParams {
  limit?: number;
  offset?: number;
  status?: 'all' | 'pending' | 'processing' | 'completed' | 'failed';
  patientId?: number;
  // 목록 확장(spec 변경 사항): 보고서별 기록 유무 조회·입구 2 "기록 있음" 판정에 사용.
  reviewId?: number;
  source?: 'text';
}

/**
 * 상담 기록 목록 응답
 */
export interface ConsultationListResponse {
  records: ConsultationRecordListItem[];
  total: number;
  limit: number;
  offset: number;
}

/**
 * 상담 기록 API 응답 래퍼
 * status: HTTP 상태 보존(설계 결정 18 — 400·404·5xx를 화면이 구분해야 하므로). 성공 응답엔 없음.
 */
interface ConsultationApiResponse<T> {
  error: number;
  data?: T;
  message?: string;
  status?: number;
}

/**
 * 텍스트 상담 기록 생성 요청 바디(설계 결정 13 — context는 DTO 키만).
 * version은 2(설계 결정 21 — 행에 guidance를 싣는 S2 이후 요청).
 */
export interface CreateTextConsultationBody {
  patientId: number;
  context: {
    source: 'text';
    version: 2;
    consultedAt: string;
    reviewId: number | null;
    drpSource: DrpSource;
    drpFormat: DrpFormat;
    drpItems: DrpChecklistRow[];
    educationItems: EducationChecklistRow[];
    note: string;
  };
}

/**
 * 텍스트 상담 기록 수정 요청 바디(서버 소유 키 제외 — reviewId·reviewDate·source·version·updatedAt 금지).
 */
export interface UpdateTextConsultationBody {
  drpSource: DrpSource;
  drpFormat: DrpFormat;
  drpItems: DrpChecklistRow[];
  educationItems: EducationChecklistRow[];
  note: string;
  consultedAt: string;
}

/** 생성 응답 data. 보고서당 1건 규칙에 걸리면 existing: true + 저장본 context. */
export interface CreateTextConsultationData {
  id: number;
  status?: string;
  createdAt?: string;
  existing?: boolean;
  context?: TextConsultationContext;
}

/**
 * 녹음 파일 업로드 및 처리 요청
 */
export const uploadConsultationRecord = async (
  file: File,
  context?: ConsultationContext,
): Promise<ConsultationApiResponse<{ id: number; status: number }>> => {
  const formData = new FormData();
  formData.append('file', file);
  if (context) {
    formData.append('context', JSON.stringify(context));
  }

  try {
    const response = await axios.post('/api/v1/consultation/records', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 300000,
    });
    return response.data;
  } catch (error) {
    log(LogLevel.ERROR, '상담 녹음 업로드 실패:', error);
    return { error: 1, message: '상담 녹음 파일을 업로드하는 데 실패했습니다.' };
  }
};

/**
 * 상담 기록 목록 조회
 */
export const fetchConsultationRecords = async (
  params?: ConsultationListParams,
): Promise<ConsultationApiResponse<ConsultationListResponse>> => {
  try {
    const response = await axios.get('/api/v1/consultation/records', { params });
    return response.data;
  } catch (error) {
    log(LogLevel.ERROR, '상담 기록 목록 조회 실패:', error);
    return { error: 1, message: '상담 기록 목록을 불러오는 데 실패했습니다.' };
  }
};

/**
 * 상담 기록 상세 조회
 */
export const fetchConsultationRecordById = async (
  id: number,
): Promise<ConsultationApiResponse<ConsultationRecord>> => {
  try {
    const response = await axios.get(`/api/v1/consultation/records/${id}`);
    return response.data;
  } catch (error: any) {
    log(LogLevel.ERROR, '상담 기록 상세 조회 실패:', error);
    return {
      error: 1,
      status: error?.response?.status,
      message: error?.response?.data?.message || '상담 기록을 불러오는 데 실패했습니다.',
    };
  }
};

/**
 * 텍스트 상담 기록 생성(설계 결정 13). 성공 시 response.data(`{ error:0, data }`)를 그대로 돌려준다.
 * 실패 시 HTTP 상태를 보존한다(설계 결정 18).
 */
export const createTextConsultationRecord = async (
  body: CreateTextConsultationBody,
): Promise<ConsultationApiResponse<CreateTextConsultationData>> => {
  try {
    const response = await axios.post('/api/v1/consultation/records/text', body);
    return response.data;
  } catch (error: any) {
    log(LogLevel.ERROR, '텍스트 상담 기록 생성 실패:', error);
    return {
      error: 1,
      status: error?.response?.status,
      message: error?.response?.data?.message || '체크리스트를 저장하지 못했습니다.',
    };
  }
};

/**
 * 텍스트 상담 기록 수정(설계 결정 6). 실패 시 HTTP 상태를 보존한다.
 */
export const updateTextConsultationRecord = async (
  id: number,
  body: UpdateTextConsultationBody,
): Promise<ConsultationApiResponse<void>> => {
  try {
    const response = await axios.patch(`/api/v1/consultation/records/${id}/text`, body);
    return response.data;
  } catch (error: any) {
    log(LogLevel.ERROR, '텍스트 상담 기록 수정 실패:', error);
    return {
      error: 1,
      status: error?.response?.status,
      message: error?.response?.data?.message || '체크리스트를 저장하지 못했습니다.',
    };
  }
};

/**
 * 처리 상태 조회
 */
export const fetchConsultationRecordStatus = async (
  id: number,
): Promise<ConsultationApiResponse<{ id: number; status: string; progress: number; currentStep: string; estimatedRemainingSeconds?: number }>> => {
  try {
    const response = await axios.get(`/api/v1/consultation/records/${id}/status`);
    return response.data;
  } catch (error) {
    log(LogLevel.ERROR, '상담 기록 상태 조회 실패:', error);
    return { error: 1, message: '상담 기록 상태를 조회하는 데 실패했습니다.' };
  }
};

/**
 * 상담 기록 삭제
 */
export const deleteConsultationRecord = async (
  id: number,
): Promise<ConsultationApiResponse<void>> => {
  try {
    const response = await axios.delete(`/api/v1/consultation/records/${id}`);
    return response.data;
  } catch (error: any) {
    log(LogLevel.ERROR, '상담 기록 삭제 실패:', error);
    return {
      error: 1,
      status: error?.response?.status,
      message: error?.response?.data?.message || '상담 기록을 삭제하는 데 실패했습니다.',
    };
  }
};

/**
 * 상담 기록 환자 연결 업데이트
 */
export const updateConsultationRecordPatient = async (
  id: number,
  patientId: number | null,
): Promise<ConsultationApiResponse<void>> => {
  try {
    const response = await axios.patch(`/api/v1/consultation/records/${id}/patient`, { patientId });
    return response.data;
  } catch (error) {
    log(LogLevel.ERROR, '상담 기록 환자 연결 실패:', error);
    return { error: 1, message: '상담 기록에 환자를 연결하는 데 실패했습니다.' };
  }
};

/**
 * 파이프라인 목록 조회
 */
export const fetchConsultationPipelines = async (): Promise<ConsultationApiResponse<{ pipelines: Array<{ id: string; name: string; description: string; isDefault: boolean; estimatedCostPer5Min: number; estimatedTimeSeconds: number }> }>> => {
  try {
    const response = await axios.get('/api/v1/consultation/pipelines');
    return response.data;
  } catch (error) {
    log(LogLevel.ERROR, '파이프라인 목록 조회 실패:', error);
    return { error: 1, message: '파이프라인 목록을 조회하는 데 실패했습니다.' };
  }
};

// ==================== Admin API ====================

/**
 * Admin 상담 기록 목록 조회 파라미터
 */
export interface AdminConsultationListParams {
  startDate: string;
  endDate: string;
  status?: string;  // "all" | "pending" | "processing" | "completed" | "failed"
  userId?: string;
  limit?: number;
  offset?: number;
}

/**
 * Admin 상담 기록 목록 응답
 */
export interface AdminConsultationListApiResponse {
  error: number;
  data?: AdminConsultationListResponse;
  message?: string;
}

/**
 * Admin 상담 기록 상세 응답
 */
export interface AdminConsultationDetailApiResponse {
  error: number;
  data?: ConsultationRecordAdmin;
  message?: string;
}

/**
 * Admin 상담 기록 목록 조회
 */
export const fetchAdminConsultationRecords = async (
  params: AdminConsultationListParams
): Promise<AdminConsultationListApiResponse> => {
  const queryParams = new URLSearchParams({
    startDate: params.startDate,
    endDate: params.endDate + ' 23:59:59',
    limit: (params.limit || 20).toString(),
    offset: (params.offset || 0).toString(),
  });

  if (params.status && params.status !== 'all') {
    queryParams.append('status', params.status);
  }
  if (params.userId) {
    queryParams.append('userId', params.userId);
  }

  const response = await axios.get(
    `/api/v1/admin/consultation/records?${queryParams}`
  );
  return response.data;
};

/**
 * Admin 상담 기록 상세 조회
 */
export const fetchAdminConsultationRecordById = async (
  id: number
): Promise<AdminConsultationDetailApiResponse> => {
  const response = await axios.get(
    `/api/v1/admin/consultation/records/${id}`
  );
  return response.data;
};

/**
 * 상태값을 한글로 변환
 */
export const getConsultationStatusLabel = (status: number): string => {
  switch (status) {
    case -1:
      return '실패';
    case 0:
      return '대기중';
    case 100:
      return 'STT 처리중';
    case 200:
      return 'LLM 처리중';
    case 300:
      return '완료';
    default:
      return '알 수 없음';
  }
};

/**
 * 상태값에 따른 배지 색상 반환
 */
export const getConsultationStatusColor = (status: number): string => {
  switch (status) {
    case -1:
      return '#ef4444'; // red
    case 0:
      return '#6b7280'; // gray
    case 100:
    case 200:
      return '#f59e0b'; // amber
    case 300:
      return '#10b981'; // green
    default:
      return '#6b7280'; // gray
  }
};
